import { linkOptions } from '@tanstack/react-router';
import type { DocumentTypeName } from '@/features/documents/constants';

export type EntityRouteCtx = {
  projectId: string;
  documentType: DocumentTypeName;
  parentResourceId: string;
  workspaceId?: string;
};

const WORKSPACE_SCOPED_ENTITY_SEGMENTS = {
  legal_deviation: 'contract-review',
  extractions: 'extractions',
  conflict_finder: 'conflicts',
  standard_deviation: 'standard-deviations',
  clause_scanner: 'clauses',
  ambiguity: 'ambiguities',
  summary: 'summary',
} as const;

export const isWorkspaceScopedEntity = (
  entity: string,
): entity is keyof typeof WORKSPACE_SCOPED_ENTITY_SEGMENTS =>
  Object.hasOwn(WORKSPACE_SCOPED_ENTITY_SEGMENTS, entity);

const BID_ENTITY_SEGMENTS = {
  bid: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
  },
  bid_draft: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/draft',
  },
  bid_cv_analysis: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/cv-analysis',
  },
  strengths_weaknesses: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
    search: { tab: 'bidOverview' },
  },
  bid_methodology: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/methodology',
  },
  bid_submission_coverage: {
    to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/compliance',
  },
} as const;

const OTHER_NAVIGATE_ENTITIES = [
  'bid_comparison',
  'kg_wp_sheet',
  'document',
  'tenderPackage',
  'tender',
  'contract',
] as const;

export type NavigateEntity =
  | keyof typeof WORKSPACE_SCOPED_ENTITY_SEGMENTS
  | keyof typeof BID_ENTITY_SEGMENTS
  | (typeof OTHER_NAVIGATE_ENTITIES)[number];

export const isNavigateEntity = (entity: unknown): entity is NavigateEntity =>
  typeof entity === 'string' &&
  (Object.hasOwn(WORKSPACE_SCOPED_ENTITY_SEGMENTS, entity) ||
    Object.hasOwn(BID_ENTITY_SEGMENTS, entity) ||
    (OTHER_NAVIGATE_ENTITIES as readonly string[]).includes(entity));

export const buildEntityRoute = (entity: string, entityId: string, ctx: EntityRouteCtx) => {
  const { projectId, documentType, parentResourceId, workspaceId } = ctx;

  const isWorkspace = documentType === 'tenderPackage' || documentType === 'contract';

  if (entity === 'tender_submission_requirements') {
    if (documentType !== 'tenderPackage') return null;
    return linkOptions({
      to: '/project/$projectId/workspaces/$workspaceId',
      params: { projectId, workspaceId: entityId || parentResourceId },
      search: { tab: 'submissionRequirements' },
    });
  }

  if (isWorkspaceScopedEntity(entity)) {
    const resolvedWorkspaceId = entityId || workspaceId;
    if (!resolvedWorkspaceId) return null;
    return linkOptions({
      to: `/project/$projectId/workspaces/$workspaceId/${WORKSPACE_SCOPED_ENTITY_SEGMENTS[entity]}`,
      params: { projectId, workspaceId: resolvedWorkspaceId },
    });
  }

  const bidEntry = Object.hasOwn(BID_ENTITY_SEGMENTS, entity)
    ? BID_ENTITY_SEGMENTS[entity as keyof typeof BID_ENTITY_SEGMENTS]
    : undefined;
  if (bidEntry) {
    const bidId =
      entity === 'bid_draft' && entityId === workspaceId
        ? documentType === 'bid'
          ? parentResourceId
          : ''
        : entityId || (documentType === 'bid' ? parentResourceId : '');
    if (documentType === 'contract' || !workspaceId || !bidId) return null;
    return linkOptions({
      ...bidEntry,
      params: { projectId, workspaceId, bidId },
    });
  }

  if (entity === 'bid_comparison') {
    const resolvedWorkspaceId =
      entityId || workspaceId || (documentType === 'tenderPackage' ? parentResourceId : '');
    if (documentType === 'contract' || !resolvedWorkspaceId) return null;
    return linkOptions({
      to: '/project/$projectId/workspaces/$workspaceId/bids/comparison',
      params: { projectId, workspaceId: resolvedWorkspaceId },
    });
  }

  if (entity === 'kg_wp_sheet') {
    if (!isWorkspace) return null;
    if (entityId)
      return linkOptions({
        to: '/project/$projectId/workspaces/$workspaceId/work-packages',
        params: { projectId, workspaceId: parentResourceId },
        search: { packageId: entityId },
      });
    return linkOptions({
      to: '/project/$projectId/workspaces/$workspaceId/scope-breakdown',
      params: { projectId, workspaceId: parentResourceId },
    });
  }

  if (entity === 'document') {
    if (!isWorkspace) return null;
    return linkOptions({
      to: '/project/$projectId/workspaces/$workspaceId/files/$documentId',
      params: { projectId, workspaceId: parentResourceId, documentId: entityId },
    });
  }

  if (isWorkspace && documentType === (entity === 'tender' ? 'tenderPackage' : entity))
    return linkOptions({
      to: '/project/$projectId/workspaces/$workspaceId',
      params: { projectId, workspaceId: parentResourceId || entityId },
    });

  return null;
};
